import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, refreshCsrf } from '@/lib/api';

export interface ExtractedWine {
  name?: string;
  winery?: string;
  vintage?: number;
  type?: string;
  classification?: string;
  alcohol?: number;
  ph?: number;
  inv_number?: string;
  grape_varieties?: string[];
  producers?: string[];
  enologists?: string[];
  agronomos?: string[];
  place?: string;
  format?: string;
  bottle_shape?: string;
  notes?: string;
}

export function useExtractWineFromPhotos() {
  return useMutation({
    mutationFn: async (photos: File[]) => {
      // Refrescar CSRF para que el upload + Claude (~30s) no expire.
      await refreshCsrf();
      const form = new FormData();
      photos.forEach((p) => form.append('photos[]', p));
      const { data } = await api.post<{ data: ExtractedWine }>(
        '/wines/extract-from-image',
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        },
      );
      return data.data;
    },
  });
}

export interface SavePhotoTastingPayload {
  photos: File[];
  wine: {
    name: string;
    winery_id?: number;
    winery_name?: string;
    vintage?: number | null;
    type?: string;
    classification?: string;
    alcohol?: number | null;
    ph?: number | null;
    inv_number?: string | null;
    format?: string;
    bottle_shape?: string | null;
    notes?: string | null;
    grape_varieties?: string[];
    producers?: string[];
    enologists?: string[];
    place_path?: string;
  };
  decision: 'cellar' | 'tasted_only' | 'both';
  score?: number | null;
  tasted_on?: string;
  tasting_event_id?: number | null;
}

export function useSavePhotoTasting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SavePhotoTastingPayload) => {
      // Refrescar CSRF antes del POST atómico (multipart, varios INSERTs).
      await refreshCsrf();
      const form = new FormData();
      payload.photos.forEach((p) => form.append('photos[]', p));
      form.append('decision', payload.decision);
      // Solo agregamos campos top-level cuando tienen valor real (no null/undefined/'').
      if (payload.score != null && !Number.isNaN(payload.score)) {
        form.append('score', String(payload.score));
      }
      if (payload.tasted_on) {
        form.append('tasted_on', payload.tasted_on);
      }
      if (payload.tasting_event_id != null && payload.tasting_event_id > 0) {
        form.append('tasting_event_id', String(payload.tasting_event_id));
      }

      // wine[...] fields — saltear null/undefined/'' para no mandar literales "null"/"undefined".
      const w = payload.wine;
      Object.entries(w).forEach(([k, v]) => {
        if (v == null || v === '') return;
        if (Array.isArray(v)) {
          const cleaned = v
            .map((item) => String(item).trim())
            .filter((s) => s.length > 0);
          cleaned.forEach((item) => form.append(`wine[${k}][]`, item));
        } else if (typeof v === 'number' && Number.isNaN(v)) {
          // skip
        } else {
          form.append(`wine[${k}]`, String(v));
        }
      });

      const { data } = await api.post<{ wine_id: number; tasting_note_id: number | null }>(
        '/tastings/from-photo',
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wines'] });
      qc.invalidateQueries({ queryKey: ['tasting-notes'] });
    },
  });
}
